export type PlayerTokens = {
  id: string;
  tokens: number;
};

export type DistributionSummary = {
  totalPlayers: number;
  playersWithTokens: number;
  averageTokensPerPlayer: number;
  maxTokensAssigned: number;
  minTokensAssigned: number;
};

/**
 * Calculates the total tokens across all players
 */
export const getTotalTokens = (playerTokens: PlayerTokens[]): number => {
  return playerTokens.reduce((sum, player) => sum + player.tokens, 0);
};

/**
 * Calculates the remaining tokens available for distribution
 */
export const getTokensRemaining = (
  playerTokens: PlayerTokens[],
  maxTokens: number,
): number => {
  const totalUsed = getTotalTokens(playerTokens);
  return Math.max(0, maxTokens - totalUsed);
};

/**
 * Calculates the maximum tokens a specific player can have
 * Takes into account current distribution and per-player limits
 */
export const calculateMaxTokensForPlayer = (
  playerId: string,
  playerTokens: PlayerTokens[],
  maxTokens: number,
  maxTokensPerPlayer: number,
): number => {
  const player = playerTokens.find((p) => p.id === playerId);
  if (!player) {
    return 0;
  }

  const totalTokensUsed = getTotalTokens(playerTokens);
  const currentPlayerTokens = player.tokens;

  // Available tokens = max total - tokens used by others + current player's tokens
  const availableTokens = maxTokens - totalTokensUsed + currentPlayerTokens;

  // Return the minimum of available tokens and per-player limit
  return Math.max(0, Math.min(availableTokens, maxTokensPerPlayer));
};

/**
 * Finds a player by ID in the player tokens array
 */
export const findPlayerById = (
  playerId: string,
  playerTokens: PlayerTokens[],
): PlayerTokens | undefined => {
  return playerTokens.find((player) => player.id === playerId);
};

/**
 * Gets the current token count for a specific player
 */
export const getTokensForPlayer = (
  playerId: string,
  playerTokens: PlayerTokens[],
): number => {
  const player = findPlayerById(playerId, playerTokens);
  return player ? player.tokens : 0;
};

/**
 * Checks if the token distribution is complete (all tokens distributed)
 */
export const isDistributionComplete = (
  playerTokens: PlayerTokens[],
  maxTokens: number,
): boolean => {
  return getTotalTokens(playerTokens) === maxTokens;
};

/**
 * Checks if more tokens can be distributed
 */
export const canDistributeMore = (
  playerTokens: PlayerTokens[],
  maxTokens: number,
): boolean => {
  return getTotalTokens(playerTokens) < maxTokens;
};

/**
 * Creates a summary of the current token distribution
 */
export const getDistributionSummary = (
  playerTokens: PlayerTokens[],
): DistributionSummary => {
  if (playerTokens.length === 0) {
    return {
      totalPlayers: 0,
      playersWithTokens: 0,
      averageTokensPerPlayer: 0,
      maxTokensAssigned: 0,
      minTokensAssigned: 0,
    };
  }

  const nonZeroPlayers = playerTokens.filter((p) => p.tokens > 0);
  const totalTokens = getTotalTokens(playerTokens);
  const tokenValues = playerTokens.map((p) => p.tokens);

  return {
    totalPlayers: playerTokens.length,
    playersWithTokens: nonZeroPlayers.length,
    averageTokensPerPlayer: totalTokens / playerTokens.length,
    maxTokensAssigned: Math.max(...tokenValues, 0),
    minTokensAssigned: Math.min(...tokenValues),
  };
};

/**
 * Generates a random token distribution
 * Uses Fisher-Yates shuffle algorithm to ensure fair randomness
 */
export const generateRandomTokenDistribution = (
  playerCount: number,
  maxTokens: number,
  maxTokensPerPlayer: number,
): number[] => {
  if (playerCount <= 0) {
    return [];
  }

  if (maxTokens < 0 || maxTokensPerPlayer <= 0) {
    return new Array(playerCount).fill(0);
  }

  const distribution = new Array(playerCount).fill(0);
  let remainingTokens = maxTokens;

  // Helper function to get random token amount within constraints
  const getRandomTokenAmount = (
    remaining: number,
    maxPerPlayer: number,
  ): number => {
    return Math.floor(
      Math.random() * Math.min(maxPerPlayer + 1, remaining + 1),
    );
  };

  // Distribute tokens to each player
  for (let i = 0; i < playerCount && remainingTokens > 0; i++) {
    const isLastPlayer = i === playerCount - 1;

    if (isLastPlayer) {
      // Give all remaining tokens to the last player (within their limit)
      distribution[i] = Math.min(remainingTokens, maxTokensPerPlayer);
    } else {
      // Give random amount to current player
      const tokensForPlayer = getRandomTokenAmount(
        remainingTokens,
        maxTokensPerPlayer,
      );
      distribution[i] = tokensForPlayer;
    }

    remainingTokens -= distribution[i];
  }

  // If we still have remaining tokens, distribute them randomly if we can
  if (remainingTokens > 0 && maxTokens <= playerCount * maxTokensPerPlayer) {
    while (remainingTokens > 0) {
      for (let i = 0; i < playerCount && remainingTokens > 0; i++) {
        const tokensForPlayer = getRandomTokenAmount(
          remainingTokens,
          maxTokensPerPlayer,
        );
        distribution[i] += tokensForPlayer;
        remainingTokens -= tokensForPlayer;
      }
    }
  }

  // Shuffle the distribution to avoid bias toward later players
  // Fisher-Yates shuffle algorithm
  for (let i = distribution.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [distribution[i], distribution[j]] = [distribution[j], distribution[i]];
  }

  return distribution;
};

/**
 * Creates initial player tokens array with zero tokens
 */
export const createInitialPlayerTokens = (
  playerIds: string[],
): PlayerTokens[] => {
  return playerIds.map((id) => ({ id, tokens: 0 }));
};

/**
 * Resets all players to zero tokens
 */
export const resetAllTokens = (
  playerTokens: PlayerTokens[],
): PlayerTokens[] => {
  return playerTokens.map((player) => ({ ...player, tokens: 0 }));
};
